import { extractOrganizedTourFlightLegs } from "./organizedTourProfileFlights";

const FALLBACK_TOUR_NAME_HE = "טיול מאורגן";

function pickTrimmed(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return "";
}

/**
 * @param {unknown} val
 * @returns {string}
 */
export function stringifyOrganizedTourDestinations(val) {
  if (val == null) return "";
  if (typeof val === "string") return val.replace(/\s+/g, " ").trim();
  if (Array.isArray(val)) {
    const parts = val
      .map((x) => {
        if (x == null) return "";
        if (typeof x === "string") return x.replace(/\s+/g, " ").trim();
        if (typeof x === "object") {
          return pickTrimmed(
            x.city,
            x.City,
            x.name,
            x.label,
            x.val,
            x.destination,
            x.Destination
          );
        }
        return String(x).replace(/\s+/g, " ").trim();
      })
      .filter(Boolean);
    return parts.join(" · ");
  }
  if (typeof val === "object") {
    return pickTrimmed(val.route, val.Route, val.text, val.label);
  }
  return String(val).replace(/\s+/g, " ").trim();
}

/**
 * @param {unknown} linkedFlights
 * @returns {string}
 */
export function routeLabelFromOrganizedTourLinkedFlights(linkedFlights) {
  const legs = extractOrganizedTourFlightLegs(linkedFlights);
  if (!legs.length) return "";
  const seq = [];
  for (const leg of legs) {
    const a = pickTrimmed(leg.depCity, leg.departureCity, leg.fromCity, leg.DepCity);
    const b = pickTrimmed(leg.arrCity, leg.arrivalCity, leg.toCity, leg.ArrCity);
    if (a && seq[seq.length - 1] !== a) seq.push(a);
    if (b && seq[seq.length - 1] !== b) seq.push(b);
  }
  if (!seq.length) return "";
  return seq.join(" → ");
}

/**
 * @param {string} tourName
 * @returns {string}
 */
export function parseOrganizedTourRouteFromTitleSplit(tourName) {
  const t = String(tourName || "").trim();
  if (!t) return "";
  const seps = [" | ", " — ", " – ", " • "];
  for (const sep of seps) {
    const i = t.indexOf(sep);
    if (i > 0 && i < t.length - sep.length) {
      const tail = t.slice(i + sep.length).replace(/\s+/g, " ").trim();
      if (tail.length >= 2) return tail;
    }
  }
  return "";
}

function pickExtended(order) {
  const ex = order?.extended;
  return ex && typeof ex === "object" ? ex : null;
}

/**
 * שם טיול + מסלול להצגה בעמוד תשלום / טעינה ישירה מ-order.
 *
 * @param {unknown} order
 * @param {{ silent?: boolean }} [options] — silent=true: no dev console (e.g. when called from info request resolver)
 * @returns {{
 *   tourName: string,
 *   tourNameSource: string,
 *   routeLabel: string,
 *   routeSource: string,
 * }}
 */
export function resolveOrganizedTourDisplaySummary(order, options = {}) {
  const silent = Boolean(options.silent);
  const orderRoot = order && typeof order === "object" ? order : null;
  const ext = pickExtended(orderRoot);

  const resv = orderRoot?.order_response?.reservation;
  const tourRes = resv?.tour && typeof resv.tour === "object" ? resv.tour : null;
  const op = orderRoot?.ourprice ?? orderRoot?.ourPrice;
  const opObj = op && typeof op === "object" ? op : null;

  let tourName = "";
  let tourNameSource = "fallback";

  const tnPairs = [
    ["order.tourName", orderRoot?.tourName],
    ["order.tour_name", orderRoot?.tour_name],
    ["order.title", orderRoot?.title],
    ["order.extended.tour.tourName", ext?.tour?.tourName],
    ["order.extended.tour.name", ext?.tour?.name],
    ["order.extended.deal.tourName", ext?.deal?.tourName],
    ["order.order_response.tourName", orderRoot?.order_response?.tourName],
    ["order.order_response.reservation.tour.tourName", tourRes?.tourName],
    ["order.order_response.reservation.tour.name", tourRes?.name],
    ["order.ourprice.tourName", opObj?.tourName],
    ["order.ourprice.tour_name", opObj?.tour_name],
  ];

  for (const [src, val] of tnPairs) {
    const s = pickTrimmed(val);
    if (s) {
      tourName = s;
      tourNameSource = src;
      break;
    }
  }
  if (!tourName) {
    tourName = FALLBACK_TOUR_NAME_HE;
    tourNameSource = "fallback";
  }

  let routeLabel = "";
  let routeSource = "empty";

  const routePairs = [
    ["order.route", stringifyOrganizedTourDestinations(orderRoot?.route)],
    ["order.destinations", stringifyOrganizedTourDestinations(orderRoot?.destinations)],
    ["order.extended.tour.route", stringifyOrganizedTourDestinations(ext?.tour?.route)],
    ["order.extended.tour.destinations", stringifyOrganizedTourDestinations(ext?.tour?.destinations)],
    ["order.extended.linkedFlights", routeLabelFromOrganizedTourLinkedFlights(ext?.linkedFlights)],
  ];

  for (const [src, val] of routePairs) {
    const s = pickTrimmed(val);
    if (s) {
      routeLabel = s.replace(/\s+/g, " ").trim();
      routeSource = src;
      break;
    }
  }

  if (!routeLabel) {
    const parsed = parseOrganizedTourRouteFromTitleSplit(tourName);
    if (parsed) {
      routeLabel = parsed;
      routeSource = "parsed_title_split";
    }
  }

  if (import.meta.env?.DEV && !silent) {
    console.info("[ORG_TOUR_PAYMENT_TOUR_NAME_RESOLVE]", {
      tourName,
      tourNameSource,
      orderId: orderRoot?.id ?? null,
    });
    console.info("[ORG_TOUR_PAYMENT_ROUTE_RESOLVE]", {
      routeLabel,
      routeSource,
      orderId: orderRoot?.id ?? null,
    });
    console.info("[ORG_TOUR_PAYMENT_DISPLAY_SUMMARY_RESOLVE]", {
      tourName,
      tourNameSource,
      routeLabel,
      routeSource,
      orderId: orderRoot?.id ?? null,
    });
  }

  return {
    tourName,
    tourNameSource,
    routeLabel,
    routeSource,
  };
}
